import {memo} from 'react';
import Link from 'next/link';
import {meetingLink} from '@/constants';
import Icon from '@/components/atoms/Icon';
import styles from './PopNavMenu.module.scss';

// Every dropdown CTA button books a meeting (external HubSpot host, so a new tab).
const CTA_PRIMARY_HREF = meetingLink;

interface CtaContent {
    title: string;
    body: string;
    primaryLabel: string;
}

const DEFAULT_CTA_CONTENT: CtaContent = {
    title: 'Build AI-Powered Software',
    body: 'Enterprise-grade development powered by AI, cloud and modern engineering.',
    primaryLabel: 'Schedule Consultation'
};

const CTA_CONTENT_BY_GROUP: Record<string, CtaContent> = {
    'Software Development': {
        title: 'Have a Software Project in Mind?',
        body: 'Turn your idea or business need into software built to perform and scale.',
        primaryLabel: 'Start Your Project'
    },
    'Mobile App Development': {
        title: 'Let’s Get Your App to Market',
        body: 'Bring your mobile product idea to life with a focused plan and experienced app team.',
        primaryLabel: 'Build Your App'
    },
    'AI Development': {
        title: 'Not sure where AI fits your business?',
        body: 'Let’s find the use cases worth investing in and put them to work.',
        primaryLabel: 'Explore AI Opportunities'
    },
    'Blockchain & Tokenization Development': {
        title: 'Have a blockchain use case in mind?',
        body: 'Let’s validate the idea and find the right approach to build it.',
        primaryLabel: 'Discuss Your Idea'
    },
    'Crypto Exchange & Wallet Development': {
        title: 'Planning an exchange or wallet?',
        body: 'Build secure wallet, exchange, and digital asset infrastructure around your business model.',
        primaryLabel: 'Discuss Your Crypto Platform'
    },
    'Hire Developers': {
        title: 'Need More Hands on Deck?',
        body: 'Tell us the skills you need, and we’ll help you build your team.',
        primaryLabel: 'Find Your Developers'
    },
    'Software Testing, QA & DevOps': {
        title: 'Need More Reliable Software Releases?',
        body: 'Strengthen quality, release stability, and deployment performance across your engineering lifecycle.',
        primaryLabel: 'Strengthen Your Delivery'
    }
};

interface DropdownCtaProps {
    onNavigate: () => void;
    groupHeading?: string;
}

const DropdownCta = memo(({onNavigate, groupHeading}: DropdownCtaProps) => {
    const content = (groupHeading && CTA_CONTENT_BY_GROUP[groupHeading]) || DEFAULT_CTA_CONTENT;

    return (
        <div className={styles.ctaColumn}>
            <div className={styles.ctaCard}>
                <div aria-hidden="true" className={styles.ctaIcon}>
                    <Icon name="Psychology" />
                </div>
                <h3 className={styles.ctaTitle}>{content.title}</h3>
                <div className={styles.ctaBody}>{content.body}</div>
                <Link href={CTA_PRIMARY_HREF} target="_blank" rel="noopener noreferrer" className="remove-underline" onClick={onNavigate}>
                    <div className={styles.ctaButton}>{content.primaryLabel}</div>
                </Link>
            </div>
        </div>
    );
});

DropdownCta.displayName = 'DropdownCta';

export default DropdownCta;
